"use client";

import { useState, useEffect } from "react";
import { useTheme } from "@/components/theme-provider";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Settings2, Loader2, Check, Sun, Moon, Monitor } from "lucide-react";
import { useSettings } from "@/hooks/use-settings";
import { toast } from "@/hooks/use-toast";
import { getErrorMessage } from "@/lib/errors";
import type { Settings } from "@convex/settings";

interface PreferencesSectionProps {
  settings: Settings;
}

export function PreferencesSection({ settings }: PreferencesSectionProps) {
  const { updateSettings } = useSettings();
  const { theme, setTheme } = useTheme();
  const [mounted, setMounted] = useState(false);

  const [isSaving, setIsSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  // Form state
  const [defaultStation, setDefaultStation] = useState(
    settings.defaultStation ?? ""
  );
  const [mileageRate, setMileageRate] = useState(
    settings.mileageRate ? (settings.mileageRate / 100).toFixed(2) : "0.67"
  );
  const [firstDayOfWeek, setFirstDayOfWeek] = useState<"sunday" | "monday">(
    settings.firstDayOfWeek
  );
  const [timeFormat, setTimeFormat] = useState<"12h" | "24h">(
    settings.timeFormat
  );
  const [mpg, setMpg] = useState(settings.mpg.toString());

  // Ensure theme toggle only renders on client to avoid hydration mismatch
  useEffect(() => {
    setMounted(true);
  }, []);

  // Track if values changed
  const hasChanges =
    (defaultStation.trim() || null) !== (settings.defaultStation || null) ||
    Math.round(parseFloat(mileageRate || "0") * 100) !== settings.mileageRate ||
    firstDayOfWeek !== settings.firstDayOfWeek ||
    timeFormat !== settings.timeFormat ||
    parseFloat(mpg || "25") !== settings.mpg;

  // Reset saved state when changes are made
  useEffect(() => {
    if (hasChanges) setSaved(false);
  }, [hasChanges]);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await updateSettings({
        defaultStation: defaultStation.trim() || null,
        mileageRate: Math.round(parseFloat(mileageRate || "0") * 100) || 67,
        firstDayOfWeek,
        timeFormat,
        mpg: parseFloat(mpg || "25") || 25,
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (error) {
      toast({
        title: "Failed to save preferences",
        description: getErrorMessage(error),
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <Settings2 className="h-5 w-5 text-muted-foreground" />
          <CardTitle className="text-lg">Preferences</CardTitle>
        </div>
        <CardDescription>Customize your experience</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Theme */}
        <div className="space-y-3">
          <Label>Theme</Label>
          <div className="flex gap-2">
            {mounted ? (
              <>
                <Button
                  type="button"
                  variant={theme === "light" ? "default" : "outline"}
                  size="sm"
                  onClick={() => setTheme("light")}
                  className="flex-1"
                >
                  <Sun className="h-4 w-4 mr-2" />
                  Light
                </Button>
                <Button
                  type="button"
                  variant={theme === "dark" ? "default" : "outline"}
                  size="sm"
                  onClick={() => setTheme("dark")}
                  className="flex-1"
                >
                  <Moon className="h-4 w-4 mr-2" />
                  Dark
                </Button>
                <Button
                  type="button"
                  variant={theme === "system" ? "default" : "outline"}
                  size="sm"
                  onClick={() => setTheme("system")}
                  className="flex-1"
                >
                  <Monitor className="h-4 w-4 mr-2" />
                  System
                </Button>
              </>
            ) : (
              <div className="flex-1 h-9 bg-muted rounded-md animate-pulse" />
            )}
          </div>
        </div>

        {/* First Day of Week */}
        <div className="space-y-3">
          <Label>First Day of Week</Label>
          <RadioGroup
            value={firstDayOfWeek}
            onValueChange={(value) =>
              setFirstDayOfWeek(value as "sunday" | "monday")
            }
            className="flex gap-4"
          >
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="sunday" id="sunday" />
              <Label htmlFor="sunday" className="font-normal">
                Sunday
              </Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="monday" id="monday" />
              <Label htmlFor="monday" className="font-normal">
                Monday
              </Label>
            </div>
          </RadioGroup>
          <p className="text-xs text-muted-foreground">
            Used for weekly statistics display
          </p>
        </div>

        {/* Time Format */}
        <div className="space-y-3">
          <Label>Time Format</Label>
          <div className="flex gap-2">
            <Button
              type="button"
              variant={timeFormat === "12h" ? "default" : "outline"}
              size="sm"
              onClick={() => setTimeFormat("12h")}
              className="flex-1"
            >
              12h
            </Button>
            <Button
              type="button"
              variant={timeFormat === "24h" ? "default" : "outline"}
              size="sm"
              onClick={() => setTimeFormat("24h")}
              className="flex-1"
            >
              24h
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            12-hour (3:30 PM) or 24-hour (15:30) clock
          </p>
        </div>

        {/* MPG */}
        <div className="space-y-2">
          <Label htmlFor="mpg">Average MPG</Label>
          <Input
            id="mpg"
            type="number"
            inputMode="decimal"
            step="0.1"
            min="1"
            placeholder="25"
            value={mpg}
            onChange={(e) => setMpg(e.target.value)}
          />
          <p className="text-xs text-muted-foreground">
            Used for fuel cost calculations
          </p>
        </div>

        {/* Default Station */}
        <div className="space-y-2">
          <Label htmlFor="defaultStation">Default Station</Label>
          <Input
            id="defaultStation"
            type="text"
            placeholder="e.g., DAX5, DLA8"
            value={defaultStation}
            onChange={(e) => setDefaultStation(e.target.value)}
          />
          <p className="text-xs text-muted-foreground">
            Auto-fills when creating new blocks
          </p>
        </div>

        {/* Mileage Rate */}
        <div className="space-y-2">
          <Label htmlFor="mileageRate">Mileage Deduction Rate</Label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
              $
            </span>
            <Input
              id="mileageRate"
              type="number"
              inputMode="decimal"
              step="0.01"
              min="0"
              value={mileageRate}
              onChange={(e) => setMileageRate(e.target.value)}
              className="pl-7"
            />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">
              /mile
            </span>
          </div>
          <p className="text-xs text-muted-foreground">
            IRS standard rate for 2025: $0.70/mile
          </p>
        </div>

        {/* Save Button */}
        <Button
          type="button"
          onClick={handleSave}
          disabled={!hasChanges || isSaving}
          className="w-full"
        >
          {isSaving ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Saving...
            </>
          ) : saved ? (
            <>
              <Check className="h-4 w-4 mr-2" />
              Saved
            </>
          ) : (
            "Save Preferences"
          )}
        </Button>
      </CardContent>
    </Card>
  );
}
